import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@app/hooks';
import {
  ptoRdImportApi,
  useCreatePtoRdImportMutation,
  useLazyGetPtoRdImportFilesQuery,
  useRecognizePtoRdImportMutation,
  useUploadPtoRdImportFileMutation,
} from '@shared/api';
import { errorText, httpStatus } from './errors';
import {
  cleanPath,
  collectSources,
  REPORT_STATE_KEY,
  uploadSources,
  type SendFile,
  type UploadProgress,
  type UploadReport,
} from './upload';

/** Этап загрузки: сбор файлов и создание пакета → отправка → постановка в очередь. */
export type UploadStage = 'prepare' | 'upload' | 'finish';

/**
 * Загрузка пакета РД: собрать файлы (ZIP — по оглавлению), отправить по
 * одному и поставить пакет в очередь распознавания. `existingId` — дозагрузка
 * в начатый пакет: файлы, которые уже на сервере (тот же путь и размер), не
 * отправляются; файл с тем же путём, но другим размером — заменяется.
 *
 * Если часть файлов не ушла, пакет не распознаётся — карточка пакета
 * предложит дозагрузить или распознать загруженное. Как только пакет создан,
 * итог (и любая ошибка) показывается в его карточке — иначе инженер загрузил
 * бы архив ещё раз, вторым пакетом.
 *
 * Уход со страницы прерывает загрузку (текущий запрос отменяется, переходов
 * после ухода нет); закрытие вкладки во время отправки браузер переспрашивает.
 */
export function useRdUpload(projectId?: string) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [createImport] = useCreatePtoRdImportMutation();
  const [uploadFile] = useUploadPtoRdImportFileMutation();
  const [recognize] = useRecognizePtoRdImportMutation();
  const [fetchFiles] = useLazyGetPtoRdImportFilesQuery();
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<UploadStage | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!busy) return;
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Старые браузеры спрашивают только при непустом returnValue.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [busy]);

  const sendTo = useCallback(
    (id: string): SendFile =>
      async (path, blob, signal) => {
        const form = new FormData();
        form.append('path', path);
        form.append('file', blob, path.split('/').pop() || 'file');
        const request = uploadFile({ id, form });
        // «Прервать» обрывает сам запрос, а не ждёт конца файла.
        const abort = () => request.abort();
        signal.addEventListener('abort', abort, { once: true });
        try {
          await request.unwrap();
        } finally {
          signal.removeEventListener('abort', abort);
        }
      },
    [uploadFile],
  );

  const start = useCallback(
    async (files: File[], existingId?: string) => {
      // Уже идёт загрузка — или страница закрыта, пока читалась брошенная папка.
      if (files.length === 0 || abortRef.current || !mountedRef.current) return;
      if (!existingId && !projectId) {
        setError('Сначала выберите проект, в который нужно загрузить РД.');
        return;
      }
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const { signal } = ctrl;
      setError(null);
      setWarnings([]);
      setProgress(null);
      setStage('prepare');
      setBusy(true);
      let id: string | null = existingId ?? null;
      const report: UploadReport = {
        uploaded: 0,
        skippedExisting: 0,
        failed: [],
        notSent: 0,
        aborted: false,
        stopped: null,
        warnings: [],
        recognizeError: null,
        error: null,
      };
      const showCard = (importId: string) => {
        if (!mountedRef.current) return;
        navigate(`/agents/pto/rd-import/${importId}`, {
          // Дозагрузка — та же карточка: не плодим записи истории.
          replace: Boolean(existingId),
          state: { [REPORT_STATE_KEY]: report },
        });
      };
      try {
        const collected = await collectSources(files);
        if (signal.aborted) return;
        report.warnings = collected.warnings;
        setWarnings(collected.warnings);
        let sources = collected.sources;
        if (sources.length === 0) {
          setError('Нет файлов для загрузки — всё пропущено (см. предупреждения выше).');
          return;
        }
        if (existingId) {
          try {
            const { files: have } = await fetchFiles(existingId).unwrap();
            const known = new Map(have.map((f) => [f.path, f.size_bytes]));
            const rest = sources.filter((s) => known.get(cleanPath(s.path)) !== s.size);
            report.skippedExisting = sources.length - rest.length;
            sources = rest;
          } catch (e) {
            if (signal.aborted) return;
            if (httpStatus(e) === 404) {
              setError('Пакет не найден — возможно, его удалили.');
              return;
            }
            report.warnings = [
              ...report.warnings,
              'Не удалось узнать, какие файлы уже загружены, — отправляем все (файлы с тем же путём заменятся).',
            ];
            setWarnings(report.warnings);
          }
        } else {
          // Цель нового пакета задаётся до отправки файлов и закрепляется на сервере.
          if (!projectId) return;
          id = (await createImport({ source_name: collected.name, project_id: projectId }).unwrap())
            .import.id;
        }
        if (!id) return;
        setStage('upload');
        const result = await uploadSources(
          sources,
          sendTo(id),
          (p) => {
            if (mountedRef.current) setProgress(p);
          },
          signal,
        );
        report.uploaded = result.uploaded;
        report.failed = result.failed;
        report.notSent = result.notSent;
        report.aborted = result.aborted;
        report.stopped = result.stopped;
        if (!mountedRef.current) return;
        if (result.stopStatus === 404) {
          setError('Пакет удалён во время загрузки — отправка остановлена.');
          return;
        }
        if (!result.aborted && !result.stopped && result.failed.length === 0) {
          setStage('finish');
          try {
            await recognize(id).unwrap();
          } catch (e) {
            report.recognizeError = errorText(
              e,
              'Файлы загружены, но пакет не встал в очередь распознавания.',
            );
          }
        }
        showCard(id);
      } catch (e) {
        if (signal.aborted && !mountedRef.current) return;
        const message = errorText(e, 'Не удалось загрузить пакет.');
        if (id) {
          report.error = message;
          showCard(id);
        } else if (mountedRef.current) {
          setError(message);
        }
      } finally {
        abortRef.current = null;
        // Карточка и список пакетов — свежие: число файлов, состояние.
        if (id) dispatch(ptoRdImportApi.util.invalidateTags(['PtoRdImport']));
        if (mountedRef.current) {
          setBusy(false);
          setStage(null);
          setProgress(null);
        }
      }
    },
    [createImport, fetchFiles, recognize, sendTo, navigate, dispatch, projectId],
  );

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  return { start, cancel, busy, stage, progress, warnings, error };
}
